import { useRef, useState, useEffect, useCallback } from "react";
import { X, Trash2, Check, Type } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

// MINI_SMOOTHING stays fixed for the small canvas modal
const MINI_SMOOTHING = 0.93;

// Chaikin's corner-cutting algorithm — rounds sharp turns.
// Each pass doubles the point count and cuts corners.
// 5 passes turns a sharp 90° angle into a very smooth curve.
function chaikin(pts, passes = 5) {
  if (pts.length < 3) return pts;
  let p = pts;
  for (let pass = 0; pass < passes; pass++) {
    const next = [p[0]];
    for (let i = 0; i < p.length - 1; i++) {
      const a = p[i], b = p[i + 1];
      next.push({ x: a.x * 0.75 + b.x * 0.25, y: a.y * 0.75 + b.y * 0.25 });
      next.push({ x: a.x * 0.25 + b.x * 0.75, y: a.y * 0.25 + b.y * 0.75 });
    }
    next.push(p[p.length - 1]);
    p = next;
  }
  return p;
}

export default function HandwritingCanvas({ onClose, onInsertText, onInsertImage, title = "Draw" }) {
  const canvasRef = useRef(null);
  const toolbarRef = useRef(null);
  const drawing = useRef(false);
  const fingerPos = useRef({ x: 0, y: 0 });   // raw finger position
  const brushPos = useRef({ x: 0, y: 0 });     // lagged brush position
  const lastBrush = useRef({ x: 0, y: 0 });    // previous brush position for stroke
  const rafId = useRef(null);
  const allStrokes = useRef([]);
  const currentStroke = useRef([]);
  const colorRef = useRef("#EDEDED");
  const sizeRef = useRef(4);
  const smoothingRef = useRef(0.85); // 0 = raw, 1 = frozen
  // Pan/zoom — 2-finger gestures move/scale the canvas view
  const panRef = useRef({ x: 0, y: 0 });
  const scaleRef = useRef(1);
  const lastPinchRef = useRef(null);
  const wrapRef = useRef(null);
  const [transform, setTransform] = useState("none");

  const [color, _setColor] = useState("#EDEDED");
  const [size, _setSize] = useState(4);
  const [smoothing, _setSmoothing] = useState(0.85);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [recognizing, setRecognizing] = useState(false);

  const setColor = v => { colorRef.current = v; _setColor(v); };
  const setSize = v => { sizeRef.current = v; _setSize(v); };
  const setSmoothing = v => { smoothingRef.current = v; _setSmoothing(v); };

  const getCtx = () => canvasRef.current?.getContext("2d");

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const toolbar = toolbarRef.current;
    if (!canvas || !toolbar) return;
    const tbH = toolbar.getBoundingClientRect().height;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight - tbH;
    const ctx = getCtx();
    ctx.fillStyle = "#111";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    redrawAll();
  }, []);

  useEffect(() => {
    const t = setTimeout(initCanvas, 60);
    window.addEventListener("resize", initCanvas);
    return () => { clearTimeout(t); window.removeEventListener("resize", initCanvas); };
  }, [initCanvas]);

  // Draw a stroke with Chaikin corner-rounding (5 passes = very smooth)
  const drawStroke = (ctx, rawPts, col, sz) => {
    if (rawPts.length < 2) return;
    const pts = chaikin(rawPts, 5);
    ctx.globalCompositeOperation = "source-over";
    ctx.strokeStyle = col;
    ctx.lineWidth = sz;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length - 1; i++) {
      const mx = (pts[i].x + pts[i+1].x) / 2;
      const my = (pts[i].y + pts[i+1].y) / 2;
      ctx.quadraticCurveTo(pts[i].x, pts[i].y, mx, my);
    }
    ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y);
    ctx.stroke();
  };

  // Draw segment (live drawing — no Chaikin, just fast line)
  const drawSegment = (ctx, x1, y1, x2, y2, col, sz) => {
    ctx.globalCompositeOperation = "source-over";
    ctx.strokeStyle = col; ctx.lineWidth = sz;
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  };

  // Redraw all completed strokes with full Chaikin smoothing
  const redrawAll = () => {
    const canvas = canvasRef.current;
    const ctx = getCtx();
    if (!canvas || !ctx) return;
    ctx.fillStyle = "#111";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    for (const stroke of allStrokes.current) {
      drawStroke(ctx, stroke.pts, stroke.color, stroke.size);
    }
  };


  // Animation loop — runs while drawing, moves brush toward finger
  const animLoop = () => {
    if (!drawing.current) return;
    const fx = fingerPos.current.x, fy = fingerPos.current.y;
    const bx = brushPos.current.x, by = brushPos.current.y;

    // Exponential decay toward finger position (the "sticky" feel)
    const newBx = bx + (fx - bx) * (1 - smoothingRef.current);
    const newBy = by + (fy - by) * (1 - smoothingRef.current);

    const dx = newBx - lastBrush.current.x;
    const dy = newBy - lastBrush.current.y;
    const moved = Math.sqrt(dx * dx + dy * dy);

    if (moved > 0.3) {
      const ctx = getCtx();
      if (ctx) {
        drawSegment(ctx, lastBrush.current.x, lastBrush.current.y, newBx, newBy, colorRef.current, sizeRef.current);
      }
      currentStroke.current.push({ x: newBx, y: newBy });
      lastBrush.current = { x: newBx, y: newBy };
    }

    brushPos.current = { x: newBx, y: newBy };
    rafId.current = requestAnimationFrame(animLoop);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const toolbar = toolbarRef.current;
    if (!canvas) return;
    const savedOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const getXY = e => {
      const r = canvas.getBoundingClientRect();
      return {
        x: (e.clientX - r.left) * (canvas.width / r.width),
        y: (e.clientY - r.top) * (canvas.height / r.height),
      };
    };

    const isToolbar = clientY => {
      const r = toolbar?.getBoundingClientRect();
      return r && clientY >= r.top && clientY <= r.bottom;
    };

    const startDraw = (x, y) => {
      drawing.current = true;
      fingerPos.current = { x, y };
      brushPos.current = { x, y };
      lastBrush.current = { x, y };
      currentStroke.current = [{ x, y }];
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(animLoop);
    };

    const moveDraw = (x, y) => {
      if (!drawing.current) return;
      fingerPos.current = { x, y };
      setHasStrokes(true);
    };

    const endDraw = () => {
      if (!drawing.current) return;
      drawing.current = false;
      cancelAnimationFrame(rafId.current);
      if (currentStroke.current.length > 1) {
        allStrokes.current.push({
          pts: [...currentStroke.current],
          color: colorRef.current,
          size: sizeRef.current,
        });
      }
      currentStroke.current = [];
    };

    // ── Pointer events (primary — works on iOS 13+, Android, desktop) ──
    const activePointers = new Map(); // pointerId → {x, y}

    const applyTransform = () => {
      const { x, y } = panRef.current;
      const s = scaleRef.current;
      const t = `translate(${x}px, ${y}px) scale(${s})`;
      setTransform(t);
    };

    const onPointerDown = e => {
      activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (activePointers.size >= 2) {
        // Two fingers — pan/zoom mode; cancel any ongoing draw
        endDraw();
        e.preventDefault();
        return;
      }
      if (isToolbar(e.clientY)) return;
      if (e.pointerType === "touch" && e.width > 30) return;
      e.preventDefault();
      canvas.setPointerCapture(e.pointerId);
      const { x, y } = getXY(e);
      startDraw(x, y);
    };

    const onPointerMove = e => {
      const prev = activePointers.get(e.pointerId);
      activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (activePointers.size === 2) {
        e.preventDefault();
        const pts = [...activePointers.values()];
        const dx = pts[1].x - pts[0].x, dy = pts[1].y - pts[0].y;
        const dist = Math.hypot(dx, dy);
        const midX = (pts[0].x + pts[1].x) / 2;
        const midY = (pts[0].y + pts[1].y) / 2;

        if (lastPinchRef.current) {
          const { dist: prevDist, midX: prevMidX, midY: prevMidY } = lastPinchRef.current;
          // Scale
          const scaleDelta = dist / prevDist;
          scaleRef.current = Math.min(5, Math.max(0.5, scaleRef.current * scaleDelta));
          // Pan
          panRef.current.x += midX - prevMidX;
          panRef.current.y += midY - prevMidY;
          applyTransform();
        }
        lastPinchRef.current = { dist, midX, midY };
        return;
      }

      lastPinchRef.current = null;
      if (!drawing.current) return;
      e.preventDefault();
      const { x, y } = getXY(e);
      moveDraw(x, y);
    };

    const onPointerUp = e => {
      activePointers.delete(e.pointerId);
      if (activePointers.size < 2) lastPinchRef.current = null;
      e.preventDefault();
      endDraw();
    };
    const onPointerCancel = e => {
      activePointers.delete(e.pointerId);
      lastPinchRef.current = null;
      e.preventDefault();
      endDraw();
    };

    // ── Touch fallback for older iOS (pre-pointer-events) ──
    const onTouchStart = e => {
      // Only fire if pointer events aren't supported on this element
      if (canvas.onpointerdown !== undefined) return;
      if (isToolbar(e.touches[0].clientY)) return;
      e.preventDefault();
      const r = canvas.getBoundingClientRect();
      const t = e.touches[0];
      startDraw(
        (t.clientX - r.left) * (canvas.width / r.width),
        (t.clientY - r.top) * (canvas.height / r.height)
      );
    };
    const onTouchMove = e => {
      if (canvas.onpointerdown !== undefined) return;
      e.preventDefault();
      if (!drawing.current) return;
      const r = canvas.getBoundingClientRect();
      const t = e.touches[0];
      moveDraw(
        (t.clientX - r.left) * (canvas.width / r.width),
        (t.clientY - r.top) * (canvas.height / r.height)
      );
    };
    const onTouchEnd = e => { if (canvas.onpointerdown !== undefined) return; e.preventDefault(); endDraw(); };
    const onTouchCancel = e => { if (canvas.onpointerdown !== undefined) return; e.preventDefault(); endDraw(); };

    canvas.addEventListener("pointerdown", onPointerDown, { passive: false });
    canvas.addEventListener("pointermove", onPointerMove, { passive: false });
    canvas.addEventListener("pointerup", onPointerUp, { passive: false });
    canvas.addEventListener("pointercancel", onPointerCancel, { passive: false });

    document.addEventListener("touchstart", onTouchStart, { passive: false });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd, { passive: false });
    document.addEventListener("touchcancel", onTouchCancel, { passive: false });

    return () => {
      document.body.style.overflow = savedOverflow;
      cancelAnimationFrame(rafId.current);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerCancel);
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchCancel);
    };
  }, []);

  const clearCanvas = () => {
    allStrokes.current = [];
    redrawAll();
    setHasStrokes(false);
  };

  const undo = () => {
    allStrokes.current.pop();
    redrawAll();
    setHasStrokes(allStrokes.current.length > 0);
  };

  const recognizeText = async () => {
    setRecognizing(true);
    try {
      const src = canvasRef.current;
      const tmp = document.createElement("canvas");
      const scale = Math.min(1, 800 / Math.max(src.width, src.height));
      tmp.width = Math.round(src.width * scale);
      tmp.height = Math.round(src.height * scale);
      const tc = tmp.getContext("2d");
      tc.fillStyle = "#111"; tc.fillRect(0, 0, tmp.width, tmp.height);
      tc.drawImage(src, 0, 0, tmp.width, tmp.height);
      const base64 = tmp.toDataURL("image/jpeg", 0.8).split(",")[1];
      const { data } = await api.post("/ai/handwriting", { image: base64, mime: "image/jpeg" });
      if (data.text) { onInsertText?.(data.text); onClose(); }
      else toast.error("No text found in drawing");
    } catch (err) {
      const s = err?.response?.status;
      const d = err?.response?.data?.detail || "";
      if (s === 404) toast.error("Deploy latest backend zip");
      else if (s === 502) toast.error(`Vision error: ${d.slice(0, 60) || "try again"}`);
      else toast.error("Recognition failed");
    }
    setRecognizing(false);
  };

  const COLORS = ["#EDEDED","#5A90F5","#FF8A00","#A855F7","#10B981","#EF4444","#FBBF24"];
  const SIZES = [3, 6, 10, 16];

  return (
    <div className="fixed inset-0 z-[500] flex flex-col" style={{ background: "#111" }}>
      <div ref={toolbarRef}
        className="flex-shrink-0 flex items-center gap-2 px-3 py-2.5 border-b border-[#2A2A2A] bg-[#0A0A0A] flex-wrap"
        style={{ position: "relative", zIndex: 502 }}
        onTouchStart={e => e.stopPropagation()}>
        <span className="text-[13px] font-semibold text-[#EDEDED]">{title}</span>
        <div className="flex items-center gap-1.5">
          {COLORS.map(c => (
            <button key={c} onPointerDown={e => { e.stopPropagation(); setColor(c); }}
              style={{ width:20, height:20, borderRadius:"50%", background:c,
                outline: color===c?`2px solid ${c}`:"none", outlineOffset:2, cursor:"pointer", flexShrink:0 }}/>
          ))}
          <input type="color" value={color} onChange={e => setColor(e.target.value)}
            onPointerDown={e => e.stopPropagation()}
            style={{ width:24, height:24, cursor:"pointer", border:"none", background:"transparent", padding:0 }}/>
        </div>
        <div className="flex items-center gap-1 border-l border-[#2A2A2A] pl-2">
          {SIZES.map(s => (
            <button key={s} onPointerDown={e => { e.stopPropagation(); setSize(s); }}
              className={`flex items-center justify-center rounded-full ${size===s?"bg-[#2A2A2A]":""}`}
              style={{ width:26, height:26, cursor:"pointer" }}>
              <div style={{ width:Math.min(s,14)+2, height:Math.min(s,14)+2, borderRadius:"50%", background:"#EDEDED" }}/>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 border-l border-[#2A2A2A] pl-2">
          <span className="text-[10px] text-[#52525B] flex-shrink-0">Smooth</span>
          <input
            type="range" min="0" max="0.98" step="0.01"
            value={smoothing}
            onChange={e => setSmoothing(parseFloat(e.target.value))}
            onPointerDown={e => e.stopPropagation()}
            className="w-20 accent-[#5A90F5]"
            style={{ height: 4 }}
          />
        </div>
        <div className="flex-1"/>
        {transform !== "none" && (
          <button onPointerDown={e => e.stopPropagation()} onClick={() => { panRef.current = {x:0,y:0}; scaleRef.current = 1; setTransform("none"); }}
            className="px-2 py-1.5 rounded-lg text-[11px] text-[#FF8A00] border border-[#FF8A00]/30 hover:bg-[#FF8A00]/10">
            Reset zoom
          </button>
        )}
        <button onPointerDown={e => e.stopPropagation()} onClick={undo} disabled={!hasStrokes}
          className="px-2.5 py-1.5 rounded-lg text-[11px] text-[#52525B] hover:text-white border border-[#2A2A2A] disabled:opacity-30">
          Undo
        </button>
        <button onPointerDown={e => e.stopPropagation()} onClick={clearCanvas}
          className="p-2 rounded-lg text-[#52525B] hover:text-white hover:bg-[#141414]">
          <Trash2 className="w-4 h-4"/>
        </button>
        {onInsertText && (
          <button onPointerDown={e => e.stopPropagation()} onClick={recognizeText}
            disabled={!hasStrokes||recognizing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#5A90F5] text-white text-[11px] font-medium disabled:opacity-40">
            <Type className="w-3.5 h-3.5"/>{recognizing?"Reading…":"To text"}
          </button>
        )}
        {onInsertImage && (
          <button onPointerDown={e => e.stopPropagation()}
            onClick={() => { onInsertImage?.(canvasRef.current.toDataURL("image/png")); onClose(); }}
            disabled={!hasStrokes}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#2A2A2A] text-[#EDEDED] text-[11px] disabled:opacity-40">
            <Check className="w-3.5 h-3.5"/> Insert
          </button>
        )}
        <button onPointerDown={e => e.stopPropagation()} onClick={onClose}
          className="p-2 rounded-lg text-[#52525B] hover:text-white hover:bg-[#141414]">
          <X className="w-4 h-4"/>
        </button>
      </div>
      <div className="flex-1 relative overflow-hidden" ref={wrapRef}>
        <canvas ref={canvasRef} style={{ display:"block", width:"100%", height:"100%", touchAction:"none", userSelect:"none", WebkitUserSelect:"none", cursor:"crosshair", transform, transformOrigin:"0 0", transition: transform === "none" ? "none" : "transform 0.05s" }}/>
        {!hasStrokes && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-[#282828] text-[14px]">Draw here</p>
          </div>
        )}
      </div>
    </div>
  );
}

export function usePenDetection() {
  const [isPen, setIsPen] = useState(false);
  useEffect(() => {
    const h = e => setIsPen(e.pointerType === "pen");
    window.addEventListener("pointerdown", h);
    return () => window.removeEventListener("pointerdown", h);
  }, []);
  return isPen;
}

// ── MiniCanvas ────────────────────────────────────────────────────────────────
// Small drawable canvas for shorthand symbol triggers.
// Click anywhere on it to open a full centered modal for easier drawing.
export function MiniCanvas({ dataUrl, onChange, width = 56, height = 40 }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      {/* Thumbnail — click to expand */}
      <div
        className="relative group cursor-pointer flex-shrink-0"
        style={{ width, height }}
        onClick={() => setExpanded(true)}
      >
        <MiniCanvasInner
          dataUrl={dataUrl}
          onChange={onChange}
          width={width}
          height={height}
          interactive={false}
        />
        {/* Expand hint */}
        <div className="absolute inset-0 flex items-center justify-center rounded bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-[9px] text-white">Edit</span>
        </div>
      </div>

      {/* Full-screen modal for drawing */}
      {expanded && (
        <MiniCanvasModal
          dataUrl={dataUrl}
          onChange={(url) => { onChange?.(url); }}
          onClose={() => setExpanded(false)}
        />
      )}
    </>
  );
}

function MiniCanvasModal({ dataUrl, onChange, onClose }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const fingerPos = useRef({ x: 0, y: 0 });
  const brushPos = useRef({ x: 0, y: 0 });
  const lastBrush = useRef({ x: 0, y: 0 });
  const rafId = useRef(null);
  const W = Math.min(window.innerWidth - 48, 480);
  const H = Math.min(window.innerHeight - 200, 320);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = W * 2; canvas.height = H * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
    ctx.fillStyle = "#0D0D0D";
    ctx.fillRect(0, 0, W, H);
    if (dataUrl) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, W, H);
      img.src = dataUrl;
    }

    const animLoop = () => {
      if (!drawing.current) return;
      const bx = brushPos.current.x + (fingerPos.current.x - brushPos.current.x) * (1 - MINI_SMOOTHING);
      const by = brushPos.current.y + (fingerPos.current.y - brushPos.current.y) * (1 - MINI_SMOOTHING);
      const dx = bx - lastBrush.current.x, dy = by - lastBrush.current.y;
      if (Math.sqrt(dx*dx+dy*dy) > 0.2) {
        const c = canvas.getContext("2d");
        c.strokeStyle = "#EDEDED"; c.lineWidth = 2;
        c.lineCap = "round"; c.lineJoin = "round";
        c.beginPath(); c.moveTo(lastBrush.current.x, lastBrush.current.y);
        c.lineTo(bx, by); c.stroke();
        lastBrush.current = { x: bx, y: by };
      }
      brushPos.current = { x: bx, y: by };
      rafId.current = requestAnimationFrame(animLoop);
    };

    const getXY = t => {
      const r = canvas.getBoundingClientRect();
      const src = t.touches ? t.touches[0] : t;
      return { x: (src.clientX - r.left) * (W / r.width), y: (src.clientY - r.top) * (H / r.height) };
    };

    const start = e => {
      e.preventDefault(); e.stopPropagation();
      drawing.current = true;
      const p = getXY(e);
      fingerPos.current = brushPos.current = lastBrush.current = p;
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(animLoop);
    };
    const move = e => {
      e.preventDefault(); e.stopPropagation();
      if (!drawing.current) return;
      fingerPos.current = getXY(e);
    };
    const end = e => { e.preventDefault(); drawing.current = false; cancelAnimationFrame(rafId.current); };

    canvas.addEventListener("touchstart", start, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", end, { passive: false });
    canvas.addEventListener("mousedown", start);
    canvas.addEventListener("mousemove", move);
    canvas.addEventListener("mouseup", end);

    return () => {
      cancelAnimationFrame(rafId.current);
      canvas.removeEventListener("touchstart", start);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", end);
      canvas.removeEventListener("mousedown", start);
      canvas.removeEventListener("mousemove", move);
      canvas.removeEventListener("mouseup", end);
    };
  }, [W, H, dataUrl]);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#0D0D0D";
    ctx.fillRect(0, 0, W, H);
  };

  const save = () => {
    onChange?.(canvasRef.current.toDataURL("image/png"));
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center bg-black/70"
      onClick={onClose}>
      <div className="flex flex-col rounded-2xl overflow-hidden border border-[#2A2A2A] shadow-2xl"
        style={{ width: W + 32, background: "#0A0A0A" }}
        onClick={e => e.stopPropagation()}>
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#1E1E1E]">
          <span className="text-[12px] font-semibold text-[#EDEDED]">Draw your symbol</span>
          <div className="flex items-center gap-2">
            <button onClick={clear} className="text-[11px] text-[#52525B] hover:text-[#EDEDED] px-2 py-1 rounded border border-[#2A2A2A]">Clear</button>
            <button onClick={save} className="text-[11px] text-white px-3 py-1 rounded bg-[#5A90F5] font-medium">Save</button>
            <button onClick={onClose} className="p-1 text-[#52525B] hover:text-[#EDEDED]"><X className="w-4 h-4"/></button>
          </div>
        </div>
        {/* Canvas */}
        <canvas ref={canvasRef}
          style={{ width: W, height: H, display: "block", margin: "16px auto", borderRadius: 8, border: "1px solid #1E1E1E", touchAction: "none", cursor: "crosshair" }}
        />
        <p className="text-[10px] text-[#2A2A2A] text-center pb-3">Draw a symbol you'll recognize — it represents this shorthand</p>
      </div>
    </div>
  );
}

// Internal canvas (non-interactive thumbnail)
function MiniCanvasInner({ dataUrl, width, height }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = width * 2; canvas.height = height * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
    ctx.fillStyle = "#0A0A0A";
    ctx.fillRect(0, 0, width, height);
    if (dataUrl) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, width, height);
      img.src = dataUrl;
    }
  }, [dataUrl, width, height]);
  return (
    <canvas ref={canvasRef}
      style={{ width, height, borderRadius: 4, border: "1px solid #2A2A2A", display: "block" }}/>
  );
}
